import cx from 'classnames'
import { forwardRef, useState, useRef } from 'react'
import { useComboBoxState } from '@react-stately/combobox'
import { AriaComboBoxProps, useComboBox } from '@react-aria/combobox'
import { useFilter } from '@react-aria/i18n'
import { ListBoxBase } from 'components/ListBox/ListBoxBase'
import Styles from './GroupComboBox.module.scss'
import { Input } from 'almanac-ui/TextInputField/Input'
import { InputProps } from 'almanac-ui/TextInputField/Types'
import { useObjectRef } from '@react-aria/utils'
import { useOverlayTriggerState } from '@react-stately/overlays'
import { Popover } from 'components/Popover/Popover'
import { Search, Plus } from 'blaze-ui/icons'
import { useListBoxLayout } from 'components/ListBox/useListBoxLayout'
import { NoResults } from './NoResults'

export interface ComboBoxProps extends AriaComboBoxProps<any>, Pick<InputProps, 'size' | 'disabled'> {
  'aria-label'?: string
  contentsClassName?: string
  inputClassName?: string
  withPlusIcon?: boolean
}

export const ComboBox = forwardRef<HTMLInputElement, ComboBoxProps>((props, fRef) => {
  // Setup filter function and state.
  const { contains } = useFilter({ sensitivity: 'base' })
  const state = useComboBoxState({ defaultFilter: contains, allowsEmptyCollection: true, ...props })
  const { withPlusIcon = false } = props

  // Setup refs and get props for child elements.
  const buttonRef = useObjectRef<HTMLButtonElement>(null)
  const inputRef = useObjectRef<HTMLInputElement>(fRef)
  const listBoxRef = useRef<HTMLDivElement>(null)
  const [popoverRef, setPopoverRef] = useState<HTMLDivElement>(document.createElement('div'))

  const { inputProps, listBoxProps, validationDetails, validationErrors } = useComboBox(
    {
      ...props,
      inputRef,
      buttonRef,
      listBoxRef,
      popoverRef: { current: popoverRef },
    },
    state,
  )

  const { isOpen, close } = useOverlayTriggerState({ isOpen: state.isOpen })

  const layout = useListBoxLayout(state, false)

  return (
    <>
      <div className={cx(Styles.contents, props.contentsClassName)}>
        {withPlusIcon ? (
          <>
            <div className={Styles.plusAndInput}>
              <Plus color="var(--selected-stroke)" />
              <Input
                className={cx(Styles.input, props.inputClassName)}
                withBorder={false}
                name="combobox"
                type="text"
                ref={inputRef}
                {...inputProps}
                value={props.inputValue}
                size="sm"
                validationDetails={validationDetails}
                validationErrors={validationErrors}
              />
            </div>
            <div className={Styles.rightSearchIcon}>
              <Search color="white" />
            </div>
          </>
        ) : (
          <>
            <Search size={20} />
            <Input
              className={cx(Styles.input, props.inputClassName)}
              withBorder={false}
              name="combobox"
              type="text"
              ref={inputRef}
              {...inputProps}
              value={props.inputValue}
              size="sm"
              validationDetails={validationDetails}
              validationErrors={validationErrors}
            />
          </>
        )}
      </div>
      {isOpen && (
        <Popover
          isOpen={isOpen}
          onClose={close}
          ref={(el) => {
            if (el) {
              setPopoverRef(el)
            }
          }}
          backdropClass={Styles.popoverBackdrop}
          containFocus={false}
          restoreFocus
          noOverlay
          autoFocus={false}
          className={Styles.popover}
        >
          {state.collection.size === 0 && <NoResults search={props.inputValue || ''} />}
          <ListBoxBase
            {...listBoxProps}
            layout={layout}
            isVirtualized
            ref={listBoxRef}
            state={state}
            onAction={props.onSelectionChange}
          />
        </Popover>
      )}
    </>
  )
})
